import { isValidPhoneNumber } from "libphonenumber-js";
import * as v from "valibot";

export const LeadNameSchema = v.pipe(
  v.string(),
  v.trim(),
  v.minLength(4, "Name must be at least 4 characters long"),
  v.regex(/\S+\s+\S+/, "Please include both first and last name")
);

export const LeadEmailSchema = v.pipe(
  v.string(),
  v.nonEmpty("Please enter your email."),
  v.email("The email is badly formatted.")
);

export const LeadPhoneNumberSchema = v.custom<string>((input) =>
  typeof input === "string" && input.length > 0 ? isValidPhoneNumber(input, "US") : false
);

export const LeadWeightSchema = v.custom<string>((input) =>
  typeof input === "string" && input.length > 0 ? parseInt(input) > 0 : false
);

export const LeadHeightSchema = v.object({
  ft: v.pipe(v.string(), v.nonEmpty("Please enter your height."), v.regex(/^\d+$/, "Height must be a number")),
  in: v.pipe(v.string(), v.nonEmpty("Please enter your height."), v.regex(/^\d+$/, "Height must be a number")),
});

export const LeadDobSchema = v.pipe(
  v.string(),
  v.nonEmpty("Please enter your date of birth."),
  v.regex(/^\d{4}-\d{2}-\d{2}$/, "Date of birth must be in the format YYYY-MM-DD")
);

export const LeadAddressSchema = v.pipe(
  v.string(),
  v.nonEmpty("Please enter your address."),
  v.minLength(4, "Address must be at least 4 characters long")
);

export const LeadRdeCommentSchema = v.pipe(
  v.string(),
  v.nonEmpty("Please enter your comment."),
  v.minLength(4, "Comment must be at least 4 characters long")
);

// insurance card is a file
export const LeadInsuranceCardSchema = v.pipe(
  v.file(),
  v.mimeType(["image/jpeg", "image/png"], "Please select a JPEG or PNG file."),
  v.maxSize(5 * 1024 * 1024, "File size must be less than 5MB")
);

export const SelectionSchema = v.object({
  key: v.string(),
  value: v.string(),
  label: v.string(),
});

export const LegalConsentSelectionSchema = v.object({
  value: v.string(),
  label: v.string(),
});

export const PostCheckoutSchema = v.pipe(
  v.object({
    appointment: v.nullable(
      v.object({
        appointment_provider_name: v.nullable(v.string()),
        appointment_date_formatted: v.nullable(v.string()),
        appointment_time_formatted: v.nullable(v.string()),
        appointment_session_type: v.nullable(v.string()),
      })
    ),
    lead: v.object({
      lead_name: LeadNameSchema,
      lead_first_name: v.nullable(v.string()),
      lead_phone_number: v.pipe(v.string(), LeadPhoneNumberSchema),
      lead_email: LeadEmailSchema,
      lead_height: LeadHeightSchema,
      lead_weight: v.pipe(v.string(), LeadWeightSchema),
      lead_address: LeadAddressSchema,
      lead_rde_comment: v.nullable(v.string()),
      lead_dob: LeadDobSchema,
      lead_front_insurance_card: LeadInsuranceCardSchema,
      lead_back_insurance_card: LeadInsuranceCardSchema,
      lead_primary_policy_holder_selection: SelectionSchema,
      lead_primary_policy_holder_relationship_selection: SelectionSchema,
      lead_primary_policy_holder_dob: v.nullable(v.string()),
      lead_primary_policy_holder_address: v.nullable(v.string()),
      lead_legal_consent_selection: LegalConsentSelectionSchema,
    }),
  }),
  v.check((data) => {
    return data.lead.lead_legal_consent_selection.value === "accept";
  }, "Please accept the legal consent to continue."),
  v.check((data) => {
    if (data.lead.lead_primary_policy_holder_selection.value !== "self") {
      const areStrings =
        typeof data.lead.lead_primary_policy_holder_dob === "string" &&
        typeof data.lead.lead_primary_policy_holder_address === "string" &&
        typeof data.lead.lead_primary_policy_holder_relationship_selection.value === "string";
      const nonNull =
        data.lead.lead_primary_policy_holder_dob !== null &&
        data.lead.lead_primary_policy_holder_address !== null &&
        data.lead.lead_primary_policy_holder_relationship_selection.value !== null;
      const nonEmpty =
        areStrings &&
        data.lead.lead_primary_policy_holder_dob!.length > 0 &&
        data.lead.lead_primary_policy_holder_address!.length > 0 &&
        data.lead.lead_primary_policy_holder_relationship_selection.value.length > 0;
      if (areStrings && nonNull && nonEmpty) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }, "Please fill out all fields for the primary policy holder.")
);
